/* Theme custom file */
/**
 * Quick add alternate
 *
 * Handles the quick-add flow for product-card-alternate:
 * button click -> fetch section -> create modal -> show -> cleanup on close.
 *
 * BRD-510: the modal reuses the native DialogComponent (@theme/dialog) so it
 * gets the native open/close animation, body scroll-lock, outside-click and
 * Escape handling, and closes on cart update -- matching the native quick-add
 * dialog and the PDP add-to-cart flow. The wrapper element carries the native
 * `quick-add-modal` class so it inherits the native fly-to-cart suppression
 * (a fly-to-cart launched from inside a modal would only animate hidden behind
 * the backdrop). The class sits on the wrapper, not the <dialog>, so the native
 * `.quick-add-modal` sizing/animation rules don't fight our custom dialog; the
 * wrapper is `display: contents` (see the section CSS) so those rules paint
 * nothing. The visible add-to-cart feedback here is the modal closing into the
 * cart drawer slide-in, like the native quick-add.
 */

import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

const SECTION_ID = 'quick-add-alternate';

/**
 * Quick-add dialog. Extends the native DialogComponent and closes itself when
 * the product is added to cart (cart:update), mirroring the native QuickAddDialog.
 */
class QuickAddAltDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /** @param {CustomEvent} event */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    // On add-to-cart the cart drawer opens on this same tick and takes over the
    // body scroll-lock. The animated closeDialog() restores the body only after
    // its ~0.2s animation, which would land after the drawer has locked and
    // unlock the background behind it. Close synchronously here so the drawer
    // locks from the correct scroll position; the animated close is kept for
    // manual dismissals (close button, backdrop, Escape).
    this.#closeForCartDrawerHandoff();
  };

  /**
   * Closes the dialog immediately and restores the body scroll state
   * synchronously (mirrors DialogComponent.closeDialog's restore step) so the
   * cart drawer opening on the same tick can re-lock from the right position.
   */
  #closeForCartDrawerHandoff() {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    const top = document.body.style.top;
    const scrollY = top ? -parseInt(top, 10) : 0;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: scrollY, behavior: 'instant' });

    dialog.close();
    this.dispatchEvent(new DialogCloseEvent());
  }
}

if (!customElements.get('quick-add-alt-dialog')) {
  customElements.define('quick-add-alt-dialog', QuickAddAltDialog);
}

/**
 * Fetches the quick-add section HTML for a given product URL.
 * @param {string} productUrl - The product URL (e.g. /products/some-handle)
 * @returns {Promise<string>} The section HTML
 */
async function fetchQuickAddSection(productUrl) {
  const url = `${productUrl}?section_id=${SECTION_ID}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch quick add section: ${response.status}`);
  }

  return response.text();
}

/**
 * Creates the quick-add dialog component and appends it to the body.
 * @param {string} html - The section HTML to inject
 * @returns {QuickAddAltDialog} The dialog component element
 */
function createModal(html, closeLabel) {
  const dialogComponent = /** @type {QuickAddAltDialog} */ (document.createElement('quick-add-alt-dialog'));
  // Native class on the wrapper (not the dialog) to inherit the fly-to-cart
  // suppression without inheriting the native modal's sizing/animation rules.
  dialogComponent.classList.add('quick-add-modal');

  dialogComponent.innerHTML = `
    <dialog class="quick-add-alt-modal" ref="dialog">
      <button class="quick-add-alt-modal__close">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.5 3.5L3.5 12.5M3.5 3.5L12.5 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
      <div class="quick-add-alt-modal__content">${html}</div>
    </dialog>
  `;

  document.body.appendChild(dialogComponent);

  const closeBtn = dialogComponent.querySelector('.quick-add-alt-modal__close');
  closeBtn?.setAttribute('aria-label', closeLabel || 'Fermer');
  closeBtn?.addEventListener('click', () => dialogComponent.closeDialog());

  // Remove the component from the DOM once its close animation has finished.
  dialogComponent.addEventListener(DialogCloseEvent.eventName, () => dialogComponent.remove(), { once: true });

  return dialogComponent;
}

// BRD-523: inline price-info panel toggle. The panel lives in the details flow
// (below the price line) so it is never clipped by the modal's overflow, unlike a
// floating popover. Single delegated listener toggles the panel and closes it on
// an outside click.
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('.quick-add-alt__tooltip-trigger');
  if (trigger) {
    e.preventDefault();
    e.stopPropagation();
    const tooltipId = trigger.getAttribute('aria-controls');
    const tooltip = document.getElementById(tooltipId);
    if (!tooltip) return;
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!isExpanded));
    tooltip.hidden = isExpanded;
    return;
  }

  // Close any open panel when clicking outside its trigger.
  for (const openTrigger of document.querySelectorAll('.quick-add-alt__tooltip-trigger[aria-expanded="true"]')) {
    const tooltip = document.getElementById(openTrigger.getAttribute('aria-controls'));
    openTrigger.setAttribute('aria-expanded', 'false');
    if (tooltip) tooltip.hidden = true;
  }
});

// Event delegation — single listener for all quick-add buttons
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.product-card-alt__quick-add');
  if (!btn) return;

  e.preventDefault();
  e.stopPropagation();

  const productUrl = btn.dataset.productUrl;
  if (!productUrl) return;

  btn.classList.add('is-loading');
  btn.disabled = true;

  try {
    const html = await fetchQuickAddSection(productUrl);
    const dialogComponent = createModal(html, btn.dataset.closeLabel);
    dialogComponent.showDialog();
  } catch (error) {
    console.error('[quick-add-alternate]', error);
  } finally {
    btn.classList.remove('is-loading');
    btn.disabled = false;
  }
});
